/**
 * Settings → Microsoft Agent 365 (Enterprise): connect the organisation's agent identity blueprint,
 * see which enterprise teams have an Entra agent identity and a registry card, and how the export
 * of their runs to Agent 365 observability is going (docs/agent365.md).
 */

import { dateShort } from '@aioe/shared/wording/dateTime';
import { Button } from '@heroui/react';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { ChoiceSelect, ConfirmDialog, ErrorNote, inputClass, PlainPanel, StatusMark } from './ui.tsx';

const SETUP = [
  'In the Microsoft Entra admin centre, open Entra ID, Agents, Agent blueprints, and create a blueprint named for AIOE (the wizard sets its owner and sponsor).',
  'Assign AgentRegistration.ReadWrite.All to the AIOE API application and the blueprint, and Agent365.Observability.OtelWrite to the blueprint, straight to each service principal (the blueprint’s consent page fails). Microsoft bars the blueprint from the registry, so cards go as the API application.',
  'Give the blueprint a credential: a federated credential naming this deployment’s managed identity (shown below when the deployment has one), or a client secret (Agent ID Administrator).',
  'Enter the blueprint’s application (client) id and choose the credential below. AIOE proves it with Entra before keeping anything; a secret is kept sealed.',
  'Every agent member of an enterprise team then gets an agent identity and a card in the Agent 365 registry, and the runs machines report for it are exported to Agent 365 observability as traces.',
];

export function Agent365Panel() {
  const view = useLoad(() => routes.agent365(), []);
  const [blueprintAppId, setBlueprintAppId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [sponsorId, setSponsorId] = useState('');
  const [credential, setCredential] = useState<'secret' | 'managed-identity'>();
  const [showSetup, setShowSetup] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [order, setOrder] = useState<{ specialistId: string; action: 'disable' | 'quarantine' | 'enable'; reason: string }>({ specialistId: '', action: 'disable', reason: '' });
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const act = async (what: string, f: () => Promise<unknown>) => {
    setError(undefined);
    setBusy(what);
    try {
      await f();
      view.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const v = view.data;
  // The managed identity is the choice when the deployment has one; a secret is the fallback.
  const chosen = credential ?? (v?.managedIdentity ? 'managed-identity' : 'secret');
  const useSecret = !v?.managedIdentity || chosen === 'secret';
  const members = v?.teams.flatMap((t) => t.members.map((m) => ({ team: t, member: m }))) ?? [];
  const registered = members.filter((x) => x.member.agent?.agentId).length;
  const waiting = members.filter((x) => !x.member.agent?.agentId);
  // Every failing card failed the same way: one notice at the top says why, and the teams only wait.
  const unavailable = v?.registry.state === 'unavailable' ? v.registry : undefined;
  const pendingOrders = v?.orders.filter((o) => o.state === 'pending') ?? [];
  // Identities in Entra that belong to no current member, as the last check found them.
  const orphans = v?.orphans.items ?? [];
  const stillEnabled = orphans.filter((o) => o.enabled);
  const disabledOnes = orphans.filter((o) => !o.enabled);
  const holderOf = (id: string) => v?.teams.flatMap((t) => t.members).find((m) => m.agent?.agentId === id)?.agent?.displayName;
  const reasonWords = (reason: string) => {
    if (reason === 'no such team') return 'Its team no longer exists';
    if (reason === 'no such member') return 'Nobody in its team has this name now';
    const keeper = reason.startsWith('duplicate of ') ? holderOf(reason.slice('duplicate of '.length)) : undefined;
    return keeper ? `A second identity for ${keeper}` : 'A second identity for a member that already has one';
  };
  const answered = (what: string) => (a: { done: string[]; failed: Array<{ message: string }> }) => {
    if (a.failed.length) throw new Error(`${a.failed.length} could not be ${what}: ${a.failed[0]!.message}`);
  };

  return (
    <PlainPanel title="Microsoft Agent 365">
      <ErrorNote error={error ?? view.error} />
      {v && !v.entraTenantId ? <p className="text-sm text-muted">This organisation does not sign in with Microsoft Entra, so Agent 365 does not apply.</p> : null}
      {v && !v.canStore && !v.managedIdentity ? <p className="mb-3 rounded-lg bg-warning/10 px-3 py-2 text-sm text-warning" title="AIOE_SECRETS_KEY">This platform has no key for keeping secrets yet, so a client secret cannot be kept here. Whoever runs this deployment sets it.</p> : null}
      {v?.entraTenantId ? (
        <dl className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-y-1.5 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]">
          <dt className="text-muted">Status</dt>
          <dd>{v.connected ? <StatusMark state="online" label="Connected" /> : <StatusMark state="idle" label="Not connected" />}</dd>
          {v.connected ? (
            <>
              <dt className="text-muted">Blueprint</dt>
              <dd>
                {v.blueprintName ?? 'Agent identity blueprint'} <span className="mono text-xs text-muted">{v.blueprintAppId}</span>
              </dd>
              <dt className="text-muted">Credential</dt>
              <dd>{v.credential === 'managed-identity' ? 'This deployment’s managed identity' : 'A client secret, kept sealed'}</dd>
              <dt className="text-muted">Connected</dt>
              <dd>{v.connectedAt ? timeAgo(v.connectedAt) : ''}</dd>
              <dt className="text-muted">Agents</dt>
              <dd className="tabular-nums">
                {registered} of {members.length} team members with an agent identity
              </dd>
              <dt className="text-muted">Licence</dt>
              <dd>
                {v.eligibility.eligible === true ? <StatusMark state="online" label="Eligible for Agent 365 observability" /> : v.eligibility.eligible === false ? <StatusMark state="error" label="Not licensed" /> : <StatusMark state="idle" label="Not checked" />}
                {v.eligibility.detail ? <div className="text-xs text-muted">{v.eligibility.detail}</div> : null}
              </dd>
              <dt className="text-muted">Exported</dt>
              <dd className="tabular-nums">
                {v.export.runs} {v.export.runs === 1 ? 'run' : 'runs'}, {v.export.toolCalls} tool {v.export.toolCalls === 1 ? 'call' : 'calls'}
                {v.export.lastAt ? <span className="text-muted"> · last {timeAgo(v.export.lastAt)}</span> : null}
              </dd>
              {v.export.lastError ? (
                <>
                  <dt className="text-muted">Last problem</dt>
                  <dd className="text-danger">{v.export.lastError}</dd>
                </>
              ) : null}
            </>
          ) : null}
        </dl>
      ) : null}

      {v?.entraTenantId && !v.connected && (v.canStore || v.managedIdentity) ? (
        <div className="mt-3 grid gap-3">
          <label className="grid gap-1 text-xs text-muted">
            Blueprint application (client) id
            <input className={`${inputClass} mono`} value={blueprintAppId} onChange={(e) => setBlueprintAppId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" spellCheck={false} />
          </label>
          {v.managedIdentity && (
            <label className="grid gap-1 text-xs text-muted">
              Credential
              <ChoiceSelect
                label="How AIOE proves it is the blueprint"
                className="w-full"
                value={chosen}
                options={{ 'managed-identity': 'This deployment’s managed identity (recommended)', ...(v.canStore ? { secret: 'A client secret' } : {}) } as Record<'secret' | 'managed-identity', string>}
                onChange={setCredential}
              />
            </label>
          )}
          {v.managedIdentity && chosen === 'managed-identity' ? (
            <div className="rounded-lg bg-surface-secondary p-3 text-xs text-muted">
              Add a federated credential on the blueprint with issuer <span className="mono break-all">{v.managedIdentity.issuer}</span>, subject <span className="mono">{v.managedIdentity.principalId}</span> and audience <span className="mono">api://AzureADTokenExchange</span>. No secret is kept anywhere.
            </div>
          ) : (
            <label className="grid gap-1 text-xs text-muted">
              Client secret
              <input className={`${inputClass} mono`} type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} autoComplete="off" />
            </label>
          )}
          <label className="grid gap-1 text-xs text-muted">
            Sponsor (Entra object id of the person accountable for the agents; you, when left empty)
            <input className={`${inputClass} mono`} value={sponsorId} onChange={(e) => setSponsorId(e.target.value)} spellCheck={false} />
          </label>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              isPending={busy === 'connect'}
              isDisabled={!blueprintAppId.trim() || (useSecret && !clientSecret)}
              onPress={() => void act('connect', () => routes.connectAgent365({ blueprintAppId: blueprintAppId.trim(), credential: useSecret ? 'secret' : 'managed-identity', ...(useSecret ? { clientSecret } : {}), ...(sponsorId.trim() ? { sponsorId: sponsorId.trim() } : {}) })).then(() => setClientSecret(''))}
            >
              Connect
            </Button>
            <Button size="sm" variant="ghost" onPress={() => setShowSetup((x) => !x)}>
              {showSetup ? 'Hide the set-up' : 'How to set it up'}
            </Button>
          </div>
          {showSetup && (
            <ol className="grid list-decimal gap-1.5 pl-5 text-sm text-muted [overflow-wrap:anywhere]">
              {SETUP.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          )}
        </div>
      ) : null}

      {v?.connected ? (
        <div className="mt-3 grid gap-3">
          {unavailable ? (
            <div className="grid gap-2 rounded-lg bg-warning/10 px-3 py-3 text-sm" role="status">
              <p className="font-medium">{unavailable.sentence}</p>
              {unavailable.step ? <p className="text-muted">{unavailable.step}</p> : null}
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="secondary" isPending={busy === 'retry-all'} isDisabled={!!busy && busy !== 'retry-all'} onPress={() => void act('retry-all', () => routes.retryAgent365All())}>
                  Try again for all
                </Button>
                <span className="text-xs text-muted">
                  {unavailable.affectedTeams} {unavailable.affectedTeams === 1 ? 'team is' : 'teams are'} waiting.
                </span>
              </div>
              {unavailable.graph ? (
                <details className="text-xs text-muted">
                  <summary className="cursor-pointer">Details for Microsoft support</summary>
                  <dl className="mt-1 grid grid-cols-[6rem_minmax(0,1fr)] gap-y-0.5">
                    <dt>Answer</dt>
                    <dd>{unavailable.graph.status}</dd>
                    {unavailable.graph.code ? (
                      <>
                        <dt>Code</dt>
                        <dd className="mono break-all">{unavailable.graph.code}</dd>
                      </>
                    ) : null}
                    {unavailable.graph.requestId ? (
                      <>
                        <dt>Request id</dt>
                        <dd className="mono break-all">{unavailable.graph.requestId}</dd>
                      </>
                    ) : null}
                    {unavailable.graph.date ? (
                      <>
                        <dt>Date</dt>
                        <dd>{unavailable.graph.date}</dd>
                      </>
                    ) : null}
                  </dl>
                </details>
              ) : null}
            </div>
          ) : null}
          {v.teams.length === 0 ? <p className="text-sm text-muted">No enterprise teams yet. Each agent member of a team you create on the Portfolio page gets an agent identity.</p> : null}
          {v.teams.length > 0 && (
            <ul className="divide-y divide-separator rounded-lg bg-surface-secondary">
              {v.teams.map((t) => {
                const done = t.members.filter((m) => m.agent?.agentId).length;
                const problems = t.members.filter((m) => m.agent?.error);
                // One problem shared by every member who has one is said once, under the team, not on each line.
                const shared = !unavailable && problems.length > 1 && problems.every((m) => m.agent!.error === problems[0]!.agent!.error) ? problems[0]!.agent!.error : undefined;
                // Identities kept for members who left or were renamed: said apart, never counted as the team's; registering again puts them right.
                const leftover = t.leftover ?? [];
                const tidy = leftover.length > 0 && !problems.length;
                return (
                  <li key={t.id} className="grid gap-1 px-3 py-2 text-sm">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <div className="min-w-0 flex-1 basis-48">
                        <div className="font-medium">{t.name}</div>
                        <div className="text-xs text-muted">
                          {t.members.length === 0 ? 'No agent members' : `${done} of ${t.members.length} agent members with an identity`}
                          {t.agent?.agentId ? ' · the team itself has an older identity' : ''}
                          {leftover.length ? ` · ${leftover.length} kept for ${leftover.length === 1 ? 'a former member' : 'former members'} (${leftover.map((a) => a.memberName ?? a.displayName).join(', ')})` : ''}
                        </div>
                      </div>
                      {t.members.length === 0 && !leftover.length ? null : unavailable && problems.length ? <StatusMark state="idle" label="Waiting for the registry" /> : tidy ? <StatusMark state="medium" label="Out of date" /> : done === t.members.length && !problems.length ? <StatusMark state="online" label="Registered" /> : problems.length ? <StatusMark state="medium" label="Needs attention" /> : <StatusMark state="idle" label="Not registered" />}
                      {tidy ? (
                        <Button size="sm" variant="secondary" isPending={busy === t.id} isDisabled={!!busy && busy !== t.id} onPress={() => void act(t.id, () => routes.registerAgent365Team(t.id))}>
                          Bring up to date
                        </Button>
                      ) : done === t.members.length && !problems.length && t.members.length > 0 ? (
                        <Button size="sm" variant="ghost" isDisabled={!!busy} onPress={() => void act(t.id, () => routes.unregisterAgent365Team(t.id))}>
                          Remove
                        </Button>
                      ) : unavailable && problems.length ? null : t.members.length > 0 ? (
                        <Button size="sm" variant="secondary" isPending={busy === t.id} isDisabled={!!busy && busy !== t.id} onPress={() => void act(t.id, () => routes.registerAgent365Team(t.id))}>
                          {done || problems.length ? 'Try again' : 'Register'}
                        </Button>
                      ) : null}
                    </div>
                    {shared ? <p className="text-xs text-danger">{shared}</p> : null}
                    <ul className="grid gap-0.5 pl-3 text-xs text-muted">
                      {t.members.map((m) => (
                        <li key={m.name} className="flex flex-wrap gap-x-2">
                          <span className="text-foreground">{m.name}</span>
                          {m.agent?.agentId ? (
                            <>
                              <span title={m.agent.appId ? `Entra application id ${m.agent.appId}` : `Entra agent ${m.agent.agentId}`}>{m.agent.adoptedFrom === 'foundry' ? 'Entra agent from Foundry' : 'Entra agent'}</span>
                              <span>{m.agent.registrationId ? 'in the registry' : unavailable && m.agent.error ? '· waiting for the registry' : 'not in the registry'}</span>
                            </>
                          ) : (
                            <span>no identity yet</span>
                          )}
                          {m.agent?.error && !shared && !unavailable ? <span className="text-danger">{m.agent.error}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="grid gap-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <p className="min-w-0 flex-1 basis-60 text-sm">
                {!v.orphans.checkedAt
                  ? 'Not yet checked for identities in Entra that belong to no current member.'
                  : v.orphans.count === 0
                    ? `Every identity in Entra belongs to a current member (checked ${timeAgo(v.orphans.checkedAt)}).`
                    : `${v.orphans.count} ${v.orphans.count === 1 ? 'identity' : 'identities'} in Entra ${v.orphans.count === 1 ? 'belongs' : 'belong'} to no current member (checked ${timeAgo(v.orphans.checkedAt)}).`}
              </p>
              {v.orphans.count > 0 ? (
                <Button size="sm" variant="secondary" onPress={() => setReviewing((x) => !x)}>
                  {reviewing ? 'Hide' : 'Review'}
                </Button>
              ) : null}
              <Button size="sm" variant="ghost" isPending={busy === 'reconcile'} isDisabled={!!busy && busy !== 'reconcile'} onPress={() => void act('reconcile', () => routes.reconcileAgent365())}>
                Check now
              </Button>
            </div>
            {reviewing && orphans.length > 0 ? (
              <>
                <ul className="divide-y divide-separator rounded-lg bg-surface-secondary text-sm">
                  {orphans.map((o) => (
                    <li key={o.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2">
                      <div className="min-w-0 flex-1 basis-56">
                        <div className="font-medium wrap-anywhere" title={`Entra object id ${o.id}`}>
                          {o.displayName || o.id}
                        </div>
                        <div className="text-xs text-muted">
                          {reasonWords(o.reason)}
                          {o.createdDateTime ? ` · created ${dateShort(o.createdDateTime)}` : ''}
                        </div>
                      </div>
                      {o.enabled ? <StatusMark state="online" label="Enabled" /> : <StatusMark state="disabled" label="Disabled" />}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="secondary" isPending={busy === 'disable-orphans'} isDisabled={!stillEnabled.length || (!!busy && busy !== 'disable-orphans')} onPress={() => void act('disable-orphans', () => routes.disableAgent365Orphans(stillEnabled.map((o) => o.id)).then(answered('disabled')))}>
                    Disable these
                  </Button>
                  <Button size="sm" variant="danger-soft" isDisabled={!disabledOnes.length || !!busy} onPress={() => setConfirmDelete(true)}>
                    Delete the disabled ones
                  </Button>
                  <span className="text-xs text-muted">Nothing is deleted until you say so. Disable first, and delete once nothing has missed them.</span>
                </div>
              </>
            ) : null}
            <ConfirmDialog
              isOpen={confirmDelete}
              title={`Delete ${disabledOnes.length} disabled ${disabledOnes.length === 1 ? 'identity' : 'identities'}`}
              confirmLabel="Delete them"
              danger
              onCancel={() => setConfirmDelete(false)}
              onConfirm={() => act('delete-orphans', () => routes.deleteAgent365Orphans(disabledOnes.map((o) => o.id)).then(answered('deleted'))).then(() => setConfirmDelete(false))}
            >
              <p>
                {disabledOnes
                  .slice(0, 5)
                  .map((o) => o.displayName || o.id)
                  .join(', ')}
                {disabledOnes.length > 5 ? ` and ${disabledOnes.length - 5} more` : ''} will be deleted from Microsoft Entra, with the roles assigned to them. This cannot be undone from here. Identities still enabled are not touched.
              </p>
              <p>An identity disabled by mistake does not need deleting: enable it again in the Entra admin centre (Agent identities, the identity, Enable), then Check now.</p>
            </ConfirmDialog>
          </div>
          <div className="grid gap-2">
            <div className="text-sm font-medium">Stop or release a specialist everywhere</div>
            <p className="text-xs text-muted">An order reaches every workbench and node on its next heartbeat and is repeated until each reports it applied. Disable stops the agent after its current turn; quarantine stops it at once and revokes its credentials; enable lets it run again.</p>
            <div className="flex flex-wrap gap-2">
              <input className={`${inputClass} min-w-56 flex-1`} aria-label="Specialist" value={order.specialistId} onChange={(e) => setOrder({ ...order, specialistId: e.target.value })} placeholder="team-id/Member name, or a specialist id" list="agent365-specialists" />
              <datalist id="agent365-specialists">
                {members.map((x) => (
                  <option key={`${x.team.id}/${x.member.name}`} value={`${x.team.id}/${x.member.name}`}>{`${x.member.name} · ${x.team.name}`}</option>
                ))}
              </datalist>
              <ChoiceSelect label="What to do" value={order.action} options={{ disable: 'Disable', quarantine: 'Quarantine', enable: 'Enable' } as Record<typeof order.action, string>} onChange={(a) => setOrder({ ...order, action: a })} />
              <Button size="sm" variant="secondary" isPending={busy === 'order'} isDisabled={!order.specialistId.trim() || (!!busy && busy !== 'order')} onPress={() => void act('order', () => routes.createAgent365Order({ action: order.action, specialistId: order.specialistId.trim(), ...(order.reason.trim() ? { reason: order.reason.trim() } : {}) }).then(() => routes.agent365())).then(() => setOrder({ ...order, specialistId: '', reason: '' }))}>
                Send the order
              </Button>
            </div>
            <input className={inputClass} aria-label="Reason" value={order.reason} onChange={(e) => setOrder({ ...order, reason: e.target.value })} placeholder="Reason the person sees, e.g. Blocked in the Microsoft 365 admin centre" />
            {pendingOrders.length > 0 && (
              <ul className="divide-y divide-separator rounded-lg bg-surface-secondary text-sm">
                {pendingOrders.map((o) => (
                  <li key={o.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                    <div className="min-w-0 flex-1 basis-48">
                      <span className="font-medium">{o.action === 'disable' ? 'Disable' : o.action === 'quarantine' ? 'Quarantine' : 'Enable'}</span> <span className="mono">{o.specialistId}</span>
                      <div className="text-xs text-muted">
                        {o.reason ? `${o.reason} · ` : ''}
                        {o.applied} {o.applied === 1 ? 'machine has' : 'machines have'} applied it{o.stillRunning ? `, ${o.stillRunning} still finishing a turn` : ''} · {timeAgo(o.requestedAt)}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" isDisabled={!!busy} onPress={() => void act(o.id, () => routes.cancelAgent365Order(o.id).then(() => routes.agent365()))}>
                      Cancel
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {waiting.length > 0 && (
              <Button size="sm" variant="secondary" isPending={busy === 'all'} isDisabled={!!busy && busy !== 'all'} onPress={() => void act('all', () => routes.registerAgent365Teams())}>
                Register every team
              </Button>
            )}
            <Button size="sm" variant="ghost" isPending={busy === 'eligibility'} isDisabled={!!busy && busy !== 'eligibility'} onPress={() => void act('eligibility', () => routes.checkAgent365Eligibility().then(() => routes.agent365()))}>
              Check the licence
            </Button>
            <Button size="sm" variant="ghost" isPending={busy === 'disconnect'} isDisabled={!!busy && busy !== 'disconnect'} onPress={() => void act('disconnect', () => routes.disconnectAgent365())}>
              Disconnect
            </Button>
            <span className="text-xs text-muted">Disconnecting forgets the secret. Agent identities already created stay in Entra.</span>
          </div>
        </div>
      ) : null}
    </PlainPanel>
  );
}
